import React, { CSSProperties } from "react";
import styled, { css } from "styled-components";
import { theme } from "../../../theme";
import { ReactComponent as BadgeClose } from "../../../assets/icons/BadgeClose.svg";

export type Color =
  | "red"
  | "gray"
  | "purple"
  | "pink"
  | "orange"
  | "yellow"
  | "green"
  | "mint"
  | "teal"
  | "blue"
  | "disabled";

export interface BadgeProps {
  color?: Color;
  style?: CSSProperties;
  value?: string;
  ghost?: boolean;
  onClose?: (e?: string) => void;
}

const Badge: React.FC<BadgeProps> = ({
  style,
  color,
  children,
  onClose,
  value,
  ghost = false,
}) => {
  return (
    <StyledBadgeWrapper style={style} color={color} ghost={ghost}>
      <div>{value || children}</div>
      {color !== "disabled" && onClose && (
        <BadgeClose
          style={{ marginLeft: "4px", cursor: "pointer" }}
          onClick={() => {
            if (onClose) onClose(value);
          }}
        />
      )}
    </StyledBadgeWrapper>
  );
};
export default Badge;
const StyledBadgeWrapper = styled.div<BadgeProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: default;

  background-color: ${({ color, ghost }) =>
    ghost ? "transparent" : getBgColor(color)};
  color: ${({ color }) => getColor(color)};

  border-radius: 100px;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  padding: 3px 10px;

  width: fit-content;

  ${({ ghost, color }) =>
    ghost &&
    css`
      border: 1px solid ${getBdColor(color)};
      padding: 2px 9px;
    `}

  & svg {
    cursor: pointer;
  }
`;

/**
 * backround color
 * @param color
 * @returns
 */
export const getBgColor = (color?: Color) => {
  switch (color) {
    case "red":
      return theme.colors.red5;
    case "gray":
      return theme.colors.gray8;
    case "purple":
      return theme.colors.mainPrimary8;
    case "pink":
      return theme.colors.pink5;
    case "orange":
      return theme.colors.orange5;
    case "yellow":
      return theme.colors.yellow5;
    case "green":
      return theme.colors.green5;
    case "mint":
      return theme.colors.mint5;
    case "teal":
      return theme.colors.teal5;
    case "blue":
      return theme.colors.blue5;
    case "disabled":
      return theme.colors.gray8;
    default:
      return theme.colors.gray8;
  }
};

/**
 * text color
 * @param color
 * @returns
 */
export const getColor = (color?: Color) => {
  switch (color) {
    case "red":
      return theme.colors.red3;
    case "gray":
      return theme.colors.gray1;
    case "purple":
      return theme.colors.mainPrimary3;
    case "pink":
      return theme.colors.pink2;
    case "orange":
      return theme.colors.orange2;
    case "yellow":
      return theme.colors.yellow1;
    case "green":
      return theme.colors.green2;
    case "mint":
      return theme.colors.mint2;
    case "teal":
      return theme.colors.teal1;
    case "blue":
      return theme.colors.blue2;
    case "disabled":
      return theme.colors.gray5;
    default:
      return theme.colors.gray1;
  }
};

/**
 * border color
 * @param color
 * @returns
 */
export const getBdColor = (color?: Color) => {
  switch (color) {
    case "red":
      return theme.colors.red4;
    case "gray":
      return theme.colors.gray7;
    case "purple":
      return theme.colors.mainPrimary6;
    case "pink":
      return theme.colors.pink4;
    case "orange":
      return theme.colors.orange4;
    case "yellow":
      return theme.colors.yellow4;
    case "green":
      return theme.colors.green4;
    case "mint":
      return theme.colors.mint4;
    case "teal":
      return theme.colors.teal4;
    case "blue":
      return theme.colors.blue4;
    default:
      return theme.colors.gray7;
  }
};
